import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import Shell from "@/components/Shell";
import { toast } from "sonner";
import { Download, ArrowRightLeft, AlertTriangle } from "lucide-react";
import { StudentAttendanceOverview, Academics, AcademicAnalytics, IdCards } from "./AdminDashboard";
import EventsPanel from "./EventsPanel";

const TABS = [
  { id: "attendance", label: "Staff Attendance" },
  { id: "student_attendance", label: "Student Attendance" },
  { id: "academics", label: "Academics" },
  { id: "acad_analytics", label: "Academic Analytics" },
  { id: "schedules", label: "Staff Schedules" },
  { id: "timetable", label: "Timetable Designer" },
  { id: "analytics", label: "Student Analytics" },
  { id: "id_cards", label: "ID Cards" },
  { id: "events", label: "Event Management" },
];

export default function FacilitatorDashboard() {
  const [tab, setTab] = useState("attendance");
  return (
    <Shell title="Facilitator Console" subtitle="Staff Coordination & Scheduling"
           tabs={TABS} active={tab} onTab={setTab}>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
        {tab === "attendance" && <StaffDailyAttendance/>}
        {tab === "student_attendance" && <StudentAttendanceOverview/>}
        {tab === "academics" && <Academics/>}
        {tab === "acad_analytics" && <AcademicAnalytics/>}
        {tab === "schedules" && <StaffSchedules/>}
        {tab === "timetable" && <TimetableDesignerEmbed/>}
        {tab === "analytics" && <ClassAnalytics/>}
        {tab === "id_cards" && <FacilitatorIdCards/>}
        {tab === "events" && <EventsPanel mode="manage"/>}
      </div>
    </Shell>
  );
}

function FacilitatorIdCards() {
  // Unified component — identical to School Admin: 4-layout selector + colour
  // swatches, visible fields, Epson 2-Up / A4 / CR-80 print, and the GridFS
  // photo upload modal for both student & staff rosters.
  return (
    <div className="space-y-4" data-testid="fac-idcards">
      <IdCards/>
    </div>
  );
}

/* ---------- Staff Daily Attendance (mirrors student attendance UX) ---------- */
function StaffDailyAttendance() {
  const TODAY = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(TODAY);
  const [dept, setDept] = useState("");
  const [roster, setRoster] = useState([]);
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState({}); // key -> status
  const [statusMap, setStatusMap] = useState({});

  useEffect(() => {
    api.get("/facilitator/staff-roster").then(r => setRoster(r.data)).catch(() => setRoster([]));
  }, []);
  useEffect(() => {
    api.get("/staff-attendance", { params: { month: date.slice(0, 7) } })
      .then(r => setLog(r.data)).catch(() => setLog([]));
    api.get("/attendance/month-status", { params: { month: date.slice(0, 7), scope: "staff" } })
      .then(r => setStatusMap(r.data?.statuses || {})).catch(() => setStatusMap({}));
  }, [date]);

  const dayStatus = statusMap[date] || {};
  const isHoliday = dayStatus.status === "holiday";
  const isUnrecorded = dayStatus.status === "unrecorded";

  const filtered = useMemo(() =>
    roster.filter(r => !dept || r.department === dept), [roster, dept]);
  const depts = useMemo(() =>
    Array.from(new Set(roster.map(r => r.department))).filter(Boolean), [roster]);

  const currStatus = (r) => {
    const key = `${r.kind}|${r.beneficiary_id}`;
    if (pending[key]) return pending[key];
    const rec = log.find(x => x.kind === r.kind && x.beneficiary_id === r.beneficiary_id && x.date === date);
    return rec?.status || "";
  };

  const cycle = (r) => {
    const key = `${r.kind}|${r.beneficiary_id}`;
    const now = currStatus(r);
    // present → half_day → absent → leave → present
    const next = now === "present" ? "half_day"
              : now === "half_day" ? "absent"
              : now === "absent" ? "leave"
              : now === "leave" ? "present"
              : "present";
    setPending(p => ({ ...p, [key]: next }));
  };

  const markAll = (status) => {
    const p = {};
    filtered.forEach(r => { p[`${r.kind}|${r.beneficiary_id}`] = status; });
    setPending(p);
  };

  const save = async () => {
    if (Object.keys(pending).length === 0) return toast.info("Nothing to save");
    setSaving(true);
    try {
      await api.post("/facilitator/staff-attendance/bulk", { date, records: pending });
      toast.success(`Saved ${Object.keys(pending).length} entries for ${date}`);
      setPending({});
      const r = await api.get("/staff-attendance", { params: { month: date.slice(0, 7) } });
      setLog(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const badge = (st) => st === "present" ? "bg-[#86EFAC]"
                     : st === "absent"   ? "bg-[#FCA5A5]"
                     : st === "leave"    ? "bg-[#FDE047]"
                     : st === "half_day" ? "bg-[#FED7AA]"
                     : "bg-white";
  const label = (st) => st === "half_day" ? "HALF DAY" : (st || "").toUpperCase();

  return (
    <div className="space-y-4">
      <div className="nb-card p-4 bg-[#93C5FD] flex flex-wrap gap-3 items-end">
        <div>
          <div className="display text-xl font-bold">Staff Attendance · {date}</div>
          <p className="text-sm mt-1">Daily marker. Click a cell to cycle <b>Present → Absent → Leave</b>. Saves push to the Admin&apos;s Staff Log.</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <label className="block"><span className="nb-label">Date</span>
            <input type="date" className="nb-input mt-1" value={date}
                   onChange={e => setDate(e.target.value)} data-testid="fac-date"/></label>
          <label className="block"><span className="nb-label">Department</span>
            <select className="nb-select mt-1" value={dept} onChange={e => setDept(e.target.value)}
                    data-testid="fac-dept">
              <option value="">All</option>
              {depts.map(d => <option key={d} value={d}>{d}</option>)}
            </select></label>
        </div>
      </div>
      {isHoliday && (
        <div className="nb-card p-3 bg-slate-200 text-slate-700 flex items-center gap-2 text-sm font-bold" data-testid="fac-holiday-banner">
          🏖️ Holiday: {dayStatus.label || "Staff Holiday"} — Attendance marking is disabled.
        </div>
      )}
      {isUnrecorded && (
        <div className="nb-card p-3 bg-amber-500/20 text-amber-600 border border-amber-400/40 flex items-center gap-2 text-sm font-bold" data-testid="fac-unrecorded-banner">
          <AlertTriangle size={15}/> Unrecorded Date — Attendance has not been saved yet
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => markAll("present")} disabled={isHoliday} className="nb-btn nb-btn-secondary text-sm" data-testid="fac-all-present">
          Mark ALL Present
        </button>
        <button onClick={() => markAll("absent")} disabled={isHoliday} className="nb-btn nb-btn-danger text-sm" data-testid="fac-all-absent">
          Mark ALL Absent
        </button>
        <button onClick={() => setPending({})} className="nb-btn nb-btn-ghost text-sm">Clear pending</button>
        <button onClick={save} disabled={saving || isHoliday}
                className="nb-btn ml-auto" data-testid="fac-save-attendance">
          {saving ? "Saving…" : `Save (${Object.keys(pending).length})`}
        </button>
      </div>
      <div className="nb-card p-0 overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-280px)] relative">
          <table className="nb-table">
            <thead><tr>
              <th className="sticky top-0 z-20 bg-white shadow-sm border-b">Name</th>
              <th className="sticky top-0 z-20 bg-white shadow-sm border-b">Kind</th>
              <th className="sticky top-0 z-20 bg-white shadow-sm border-b">Designation</th>
              <th className="sticky top-0 z-20 bg-white shadow-sm border-b">Department</th>
              <th className="sticky top-0 z-20 bg-white shadow-sm border-b">Status</th>
            </tr></thead>
            <tbody>
              {filtered.map(r => {
                const key = `${r.kind}|${r.beneficiary_id}`;
                const st = currStatus(r);
                return (
                  <tr key={key} data-testid={`fac-row-${key}`}>
                    <td className="font-bold">{r.name}</td>
                    <td className="text-xs uppercase mono">{r.kind}</td>
                    <td>{r.designation}</td>
                    <td>{r.department}</td>
                    <td>
                      <button onClick={() => !isHoliday && cycle(r)} disabled={isHoliday}
                              className={`nb-btn ${badge(st)} px-3 py-1 text-xs min-w-[100px] justify-center ${isHoliday?"opacity-50 cursor-not-allowed":""}`}
                              data-testid={`fac-cell-${key}`}>
                        {st ? label(st) : "—"}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500">No staff match filter</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------- Staff Schedules — Free tracker + individual timetable ---------- */
function StaffSchedules() {
  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const PERIODS = [1, 2, 3, 4, 5, 6, 7, 8];
  const today = new Date();
  const dayIdx = today.getDay(); // 0=Sun, 6=Sat
  const dayCode = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dayIdx];
  const [day, setDay] = useState(DAYS.includes(dayCode) ? dayCode : "Mon");
  const [period, setPeriod] = useState(1);
  const [free, setFree] = useState([]);
  const [busy, setBusy] = useState(0);
  const [roster, setRoster] = useState([]);
  const [selectedName, setSelectedName] = useState("");
  const [schedule, setSchedule] = useState([]);
  const [subOpen, setSubOpen] = useState(null); // free-staff row to promote

  const loadFree = async () => {
    const r = await api.get("/facilitator/free-staff", { params: { day, period } });
    setFree(r.data.free || []); setBusy(r.data.busy_count || 0);
  };
  useEffect(() => { loadFree(); }, [day, period]);
  useEffect(() => {
    api.get("/facilitator/staff-roster").then(r => {
      const teachers = (r.data || []).filter(x => x.department === "Teaching");
      setRoster(teachers);
    });
  }, []);

  const loadSchedule = async (name) => {
    setSelectedName(name);
    if (!name) { setSchedule([]); return; }
    const r = await api.get("/facilitator/staff-schedule", { params: { name } });
    setSchedule(r.data || []);
  };

  const grid = useMemo(() => {
    const g = {};
    schedule.forEach(s => { g[`${s.day_of_week}|${s.period}`] = s; });
    return g;
  }, [schedule]);

  const submitSubstitute = async (payload) => {
    try {
      await api.post("/facilitator/substitute", payload);
      toast.success(`Substitute assigned: ${payload.substitute_name} → ${payload.class_name}-${payload.section}`);
      setSubOpen(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="nb-card p-4 bg-[#FDE047] flex flex-wrap gap-3 items-end">
        <div>
          <div className="display text-xl font-bold">Free-Staff Tracker</div>
          <p className="text-sm mt-1">Instantly list teachers with an empty slot at <b>{day} · P{period}</b>.</p>
        </div>
        <label className="ml-auto"><div className="nb-label">Day</div>
          <select value={day} onChange={e => setDay(e.target.value)} className="nb-select mt-1"
                  data-testid="fs-day">
            {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
          </select></label>
        <label><div className="nb-label">Period</div>
          <select value={period} onChange={e => setPeriod(parseInt(e.target.value))} className="nb-select mt-1"
                  data-testid="fs-period">
            {PERIODS.map(p => <option key={p} value={p}>P{p}</option>)}
          </select></label>
      </div>

      <div className="nb-card p-0 overflow-hidden">
        <div className="px-4 py-2 border-b-2 border-black bg-white flex items-center gap-2">
          <div className="font-bold">Available teachers ({free.length})</div>
          <div className="text-xs text-slate-600">Currently assigned: {busy}</div>
        </div>
        <table className="nb-table">
          <thead><tr><th>Name</th><th>Subject</th><th>Class</th><th></th></tr></thead>
          <tbody>
            {free.map(f => (
              <tr key={f.beneficiary_id} data-testid={`free-row-${f.beneficiary_id}`}>
                <td className="font-bold">{f.name}</td>
                <td>{f.subject || "—"}</td>
                <td>{f.class_name ? `${f.class_name}-${f.section}` : "—"}</td>
                <td>
                  <button onClick={() => setSubOpen({ ...f, day, period })}
                          className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                          data-testid={`assign-sub-${f.beneficiary_id}`}>
                    <ArrowRightLeft size={12}/> Assign Substitute
                  </button>
                </td>
              </tr>
            ))}
            {free.length === 0 && <tr><td colSpan={4} className="text-center py-4 text-slate-500">Nobody is free at this slot</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="nb-card p-4 bg-white">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="display text-lg font-bold">Teacher Schedule</div>
            <p className="text-xs">Pick a teaching-staff to view their weekly allocation.</p>
          </div>
          <label className="ml-auto"><div className="nb-label">Staff</div>
            <select value={selectedName} onChange={e => loadSchedule(e.target.value)}
                    className="nb-select mt-1 min-w-[220px]" data-testid="fs-teacher">
              <option value="">-- pick a teacher --</option>
              {roster.map(t => <option key={t.beneficiary_id} value={t.name}>{t.name}</option>)}
            </select></label>
        </div>
        {selectedName && (
          <div className="mt-4 overflow-x-auto">
            <table className="nb-table">
              <thead><tr><th>Period</th>{DAYS.map(d => <th key={d}>{d}</th>)}</tr></thead>
              <tbody>
                {PERIODS.map(p => (
                  <tr key={p}>
                    <td className="font-bold mono">P{p}</td>
                    {DAYS.map(d => {
                      const s = grid[`${d}|${p}`];
                      return (
                        <td key={d} className={s ? "bg-[#FDE047]" : ""}>
                          {s ? (
                            <div>
                              <div className="font-bold text-xs">{s.subject}</div>
                              <div className="text-[10px]">{s.class_name}-{s.section}</div>
                              {s.start_time && <div className="text-[10px] mono">{s.start_time}-{s.end_time}</div>}
                            </div>
                          ) : <span className="text-slate-300 text-xs">free</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {subOpen && (
        <SubstituteModal free={subOpen} onClose={() => setSubOpen(null)} onSubmit={submitSubstitute}/>
      )}
    </div>
  );
}

function SubstituteModal({ free, onClose, onSubmit }) {
  const [className, setClassName] = useState("");
  const [section, setSection] = useState("");
  const [subject, setSubject] = useState(free.subject || "");
  const [absentName, setAbsentName] = useState("");
  const [settings, setSettings] = useState({ grades: [], sections: [] });
  useEffect(() => { api.get("/settings").then(r => setSettings(r.data)); }, []);
  const submit = () => {
    if (!className || !section) return toast.error("Class & section required");
    onSubmit({
      day: free.day, period: free.period,
      class_name: className, section, subject,
      substitute_name: free.name, absent_name: absentName,
    });
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className="nb-card p-6 bg-white max-w-md w-full space-y-3" onClick={e => e.stopPropagation()}>
        <h3 className="display text-xl font-bold">Assign Substitute</h3>
        <p className="text-sm"><b>{free.name}</b> will cover <b>{free.day} · P{free.period}</b>.</p>
        <div className="grid grid-cols-2 gap-2">
          <label><div className="nb-label">Class</div>
            <select value={className} onChange={e => setClassName(e.target.value)} className="nb-select">
              <option value="">—</option>
              {settings.grades?.map(g => <option key={g} value={g}>{g}</option>)}
            </select></label>
          <label><div className="nb-label">Section</div>
            <select value={section} onChange={e => setSection(e.target.value)} className="nb-select">
              <option value="">—</option>
              {settings.sections?.map(s => <option key={s} value={s}>{s}</option>)}
            </select></label>
        </div>
        <label className="block"><div className="nb-label">Subject</div>
          <input className="nb-input" value={subject} onChange={e => setSubject(e.target.value)}/></label>
        <label className="block"><div className="nb-label">Absent teacher (optional)</div>
          <input className="nb-input" value={absentName} onChange={e => setAbsentName(e.target.value)}
                 placeholder="e.g. Ms. Anika Rao"/></label>
        <div className="flex gap-2">
          <button onClick={onClose} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
          <button onClick={submit} className="nb-btn flex-1 justify-center" data-testid="sub-confirm">
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Timetable Designer Embed (mirrors Admin) ---------- */
function TimetableDesignerEmbed() {
  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const PERIODS = [1, 2, 3, 4, 5, 6, 7, 8];
  const [settings, setSettings] = useState({ grades: [], sections: [], subjects: [] });
  const [staff, setStaff] = useState([]);
  const [cls, setCls] = useState("");
  const [sec, setSec] = useState("");
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  useEffect(() => {
    api.get("/settings").then(r => setSettings(r.data));
    api.get("/staff").then(r => setStaff(r.data.filter(x => x.department === "Teaching")))
       .catch(() => setStaff([]));
  }, []);
  const load = () => {
    if (!cls || !sec) return;
    api.get("/timetables", { params: { class_name: cls, section: sec } })
       .then(r => setRows(r.data));
  };
  useEffect(() => { load(); }, [cls, sec]);   // eslint-disable-line

  const grid = useMemo(() => {
    const g = {}; rows.forEach(r => { g[`${r.day_of_week}|${r.period}`] = r; }); return g;
  }, [rows]);

  const openCell = (day, period) => {
    const existing = grid[`${day}|${period}`];
    setEdit({
      day_of_week: day, period,
      subject: existing?.subject || "", educator_name: existing?.educator_name || "",
      start_time: existing?.start_time || "", end_time: existing?.end_time || "",
      tt_id: existing?.tt_id || null,
    });
  };
  const save = async () => {
    if (!edit.subject) return toast.error("Subject required");
    try {
      await api.post("/timetables", {
        class_name: cls, section: sec, day_of_week: edit.day_of_week, period: edit.period,
        subject: edit.subject, educator_name: edit.educator_name,
        start_time: edit.start_time, end_time: edit.end_time,
      });
      toast.success("Saved · both consoles synced"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const clearCell = async () => {
    if (!edit?.tt_id) return setEdit(null);
    if (!window.confirm("Clear this slot?")) return;
    await api.delete(`/timetables/${edit.tt_id}`);
    toast.success("Cleared"); setEdit(null); load();
  };

  return (
    <div className="space-y-4">
      <div className="nb-card p-4 bg-[#93C5FD] flex flex-wrap items-end gap-3">
        <div>
          <div className="display text-xl font-bold">Time Table Designer</div>
          <p className="text-sm mt-1">Same editor as the Admin Console — edits auto-sync both sides.</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <label><div className="nb-label">Class</div>
            <select value={cls} onChange={e => setCls(e.target.value)} className="nb-select w-32 mt-1"
                    data-testid="fac-tt-class">
              <option value="">—</option>
              {settings.grades?.map(g => <option key={g} value={g}>{g}</option>)}
            </select></label>
          <label><div className="nb-label">Section</div>
            <select value={sec} onChange={e => setSec(e.target.value)} className="nb-select w-24 mt-1"
                    data-testid="fac-tt-section">
              <option value="">—</option>
              {settings.sections?.map(s => <option key={s} value={s}>{s}</option>)}
            </select></label>
        </div>
      </div>
      {(!cls || !sec) ? (
        <div className="nb-card p-6 text-slate-500">Pick class + section to start.</div>
      ) : (
        <div className="nb-card p-0 overflow-x-auto">
          <table className="nb-table">
            <thead><tr><th>Period</th>{DAYS.map(d => <th key={d}>{d}</th>)}</tr></thead>
            <tbody>
              {PERIODS.map(p => (
                <tr key={p}>
                  <td className="font-bold mono">P{p}</td>
                  {DAYS.map(d => {
                    const s = grid[`${d}|${p}`];
                    return (
                      <td key={d} onClick={() => openCell(d, p)}
                          className={`cursor-pointer hover:bg-[#FDE047] ${s ? "bg-[#FDE047]" : ""}`}
                          data-testid={`fac-tt-cell-${d}-${p}`}>
                        {s ? (
                          <div>
                            <div className="font-bold text-sm">{s.subject}</div>
                            {s.educator_name && <div className="text-xs">{s.educator_name}</div>}
                            {s.start_time && <div className="text-xs mono">{s.start_time}-{s.end_time}</div>}
                          </div>
                        ) : <span className="text-slate-300 text-xs">click to add</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
             onClick={() => setEdit(null)}>
          <div className="nb-card p-6 bg-white max-w-md w-full space-y-3"
               onClick={e => e.stopPropagation()}>
            <h3 className="display text-xl font-bold">{edit.day_of_week} · Period {edit.period}</h3>
            <label className="block"><span className="nb-label">Subject *</span>
              <select className="nb-select" value={edit.subject}
                      onChange={e => setEdit({ ...edit, subject: e.target.value })}
                      data-testid="fac-tt-edit-subject">
                <option value="">-- pick a subject --</option>
                {(settings.subjects || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select></label>
            <label className="block"><span className="nb-label">Educator</span>
              <select className="nb-select" value={edit.educator_name}
                      onChange={e => setEdit({ ...edit, educator_name: e.target.value })}
                      data-testid="fac-tt-edit-educator">
                <option value="">-- pick an educator --</option>
                {staff.map(s => (
                  <option key={s.beneficiary_id} value={s.name}>
                    {s.name}{s.subject ? ` · ${s.subject}` : ""}
                  </option>
                ))}
              </select></label>
            <div className="grid grid-cols-2 gap-2">
              <label><span className="nb-label">Start</span>
                <input type="time" className="nb-input" value={edit.start_time}
                       onChange={e => setEdit({ ...edit, start_time: e.target.value })}/></label>
              <label><span className="nb-label">End</span>
                <input type="time" className="nb-input" value={edit.end_time}
                       onChange={e => setEdit({ ...edit, end_time: e.target.value })}/></label>
            </div>
            <div className="flex gap-2 pt-2">
              {edit.tt_id && (
                <button onClick={clearCell} className="nb-btn nb-btn-danger px-3">Clear</button>
              )}
              <button onClick={() => setEdit(null)} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={save} className="nb-btn flex-1 justify-center" data-testid="fac-tt-edit-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Student Analytics (Day/Month toggle, per class-section) ---------- */
function SummaryCard({ title, color, testid, stats, foot, extra }) {
  const tint = (tone) => tone === "green" ? "bg-[#86EFAC]"
                     : tone === "red" ? "bg-[#FCA5A5]"
                     : "bg-white";
  return (
    <div className={`nb-card p-4 ${color}`} data-testid={testid}>
      <div className="display text-lg font-bold">{title}</div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        {stats.map(s => (
          <div key={s.label} className={`nb-card p-2 ${tint(s.tone)}`}>
            <div className="text-[10px] uppercase mono">{s.label}</div>
            <div className="display text-2xl font-black">{s.value}</div>
          </div>
        ))}
      </div>
      {foot && <div className="text-xs mono mt-2">{foot}</div>}
      {extra}
    </div>
  );
}

function ClassAnalytics() {
  const TODAY = new Date().toISOString().slice(0, 10);
  const THIS_MONTH = TODAY.slice(0, 7);
  const [mode, setMode] = useState("day");   // "day" | "month"
  const [day, setDay] = useState(TODAY);
  const [month, setMonth] = useState(THIS_MONTH);
  const [data, setData] = useState({ rows: [] });
  const [summary, setSummary] = useState(null);
  const [drill, setDrill] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => {
    const params = mode === "day" ? { day } : { month };
    api.get("/facilitator/analytics", { params })
      .then(r => setData(r.data)).catch(() => setData({ rows: [] }));
  }, [mode, day, month]);
  useEffect(() => {
    // Summary always uses a day — use current picker in day-mode, first-of-month in month-mode
    const d = mode === "day" ? day : `${month}-01`;
    api.get("/facilitator/analytics/summary", { params: { day: d } })
      .then(r => setSummary(r.data)).catch(() => setSummary(null));
  }, [mode, day, month]);

  const pickedLabel = mode === "day"
    ? new Date(day).toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" })
    : new Date(`${month}-01`).toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const exportCsv = () => {
    const isDay = mode === "day";
    const header = isDay
      ? ["Class","Section","Total Students","Present","Absent","Not-marked"]
      : ["Class","Section","Total Students","Working Days","Total Sessions","Present Marks","Absent Marks"];
    const rows = (data.rows || []).map(r => (isDay ? [
      r.class_name, r.section, r.total_students,
      r.present_marks, r.absent_marks, r.not_marked ?? Math.max(r.total_students - r.present_marks - r.absent_marks, 0),
    ] : [
      r.class_name, r.section, r.total_students, r.working_days,
      r.student_days_possible, r.present_marks, r.absent_marks,
    ]).join(","));
    const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `class-attendance-${isDay ? day : month}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="nb-card p-4 bg-[#C4B5FD] flex flex-wrap items-end gap-3">
        <div>
          <div className="display text-xl font-bold">Class-wise Attendance Analytics</div>
          <p className="text-sm mt-1">Numerical trends split by Class + Section for <b>{pickedLabel}</b>.</p>
        </div>
        <label className="ml-auto"><div className="nb-label">Range</div>
          <select value={mode} onChange={e => setMode(e.target.value)}
                  className="nb-select mt-1" data-testid="an-mode">
            <option value="day">Day</option>
            <option value="month">Month</option>
          </select></label>
        {mode === "day" ? (
          <label><div className="nb-label">Day</div>
            <input type="date" className="nb-input mt-1" value={day}
                   onChange={e => setDay(e.target.value)} data-testid="an-day"/></label>
        ) : (
          <label><div className="nb-label">Month</div>
            <input type="month" className="nb-input mt-1" value={month}
                   onChange={e => setMonth(e.target.value)} data-testid="an-month"/></label>
        )}
        <button onClick={exportCsv} className="nb-btn nb-btn-secondary" data-testid="an-export">
          <Download size={14}/> Export CSV
        </button>
      </div>
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="fac-summary-grid">
          <SummaryCard title="Staff — Today" color="bg-[#93C5FD]" testid="fac-summary-staff"
                       stats={[
                         { label: "Total", value: summary.staff.total },
                         { label: "Present", value: summary.staff.present, tone: "green" },
                         { label: "Absent", value: summary.staff.absent, tone: "red" },
                       ]}
                       foot={`½-day: ${summary.staff.half_day} · leave: ${summary.staff.leave}`}/>
          <SummaryCard title="Students — Today" color="bg-[#86EFAC]" testid="fac-summary-students"
                       stats={[
                         { label: "Total", value: summary.students.total },
                         { label: "Present", value: summary.students.present, tone: "green" },
                         { label: "Absent", value: summary.students.absent, tone: "red" },
                       ]}
                       foot={`Not-marked yet: ${summary.students.not_marked}`}/>
          <SummaryCard title="Class Submissions" color="bg-[#FDE047]" testid="fac-summary-classes"
                       stats={[
                         { label: "Total", value: summary.classes.total },
                         { label: "Marked", value: summary.classes.marked, tone: "green" },
                         { label: "Not Marked", value: summary.classes.not_marked, tone: "red" },
                       ]}
                       extra={
                         <button onClick={() => setDrill(true)}
                                 className="nb-btn nb-btn-secondary text-xs mt-2"
                                 data-testid="fac-summary-drill">
                           View classes →
                         </button>}/>
        </div>
      )}
      {drill && summary && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
             onClick={() => setDrill(false)}>
          <div className="nb-card p-6 bg-white max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()} data-testid="fac-drill-modal">
            <h3 className="display text-xl font-bold">Class-wise Attendance Status · {pickedLabel}</h3>
            <input placeholder="Search class or section…" value={q}
                   onChange={e => setQ(e.target.value)}
                   className="nb-input mt-3" data-testid="fac-drill-search"/>
            <div className="mt-2 overflow-y-auto flex-1 border-2 border-black">
              <table className="nb-table">
                <thead><tr><th>Class</th><th>Section</th><th>Status</th></tr></thead>
                <tbody>
                  {summary.classes.rows
                    .filter(r => !q ||
                      r.class_name.toLowerCase().includes(q.toLowerCase()) ||
                      r.section.toLowerCase().includes(q.toLowerCase()))
                    .map(r => (
                    <tr key={`${r.class_name}-${r.section}`}
                        data-testid={`drill-row-${r.class_name}-${r.section}`}>
                      <td className="font-bold">{r.class_name}</td>
                      <td>{r.section}</td>
                      <td>
                        <span className={"nb-badge " + (r.status === "Marked" ? "bg-[#86EFAC]" : "bg-[#FCA5A5]")}>
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={() => setDrill(false)}
                    className="nb-btn nb-btn-ghost mt-3 w-full justify-center">Close</button>
          </div>
        </div>
      )}
      {(data.rows || []).length === 0 ? (
        <div className="nb-card p-6 text-slate-500">No student attendance recorded for {pickedLabel}.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.rows.map(r => {
            const isDay = mode === "day";
            const possible = isDay ? r.total_students : r.student_days_possible;
            const rate = possible > 0 ? Math.round((r.present_marks / possible) * 100) : 0;
            const barW = Math.min(100, rate);
            const notMarked = isDay ? (r.not_marked ?? Math.max(r.total_students - r.present_marks - r.absent_marks, 0)) : null;
            return (
              <div key={`${r.class_name}-${r.section}`} className="nb-card p-4 bg-white"
                   data-testid={`an-row-${r.class_name}-${r.section}`}>
                <div className="flex items-center justify-between">
                  <div className="display text-lg font-bold">Class {r.class_name} · Sec {r.section}</div>
                  <div className="text-xs nb-badge bg-[#FDE047]">{r.total_students} students</div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="nb-card p-2 bg-[#86EFAC]">
                    <div className="text-[10px] uppercase mono">Present</div>
                    <div className="display text-2xl font-black">{r.present_marks}</div>
                  </div>
                  <div className="nb-card p-2 bg-[#FCA5A5]">
                    <div className="text-[10px] uppercase mono">Absent</div>
                    <div className="display text-2xl font-black">{r.absent_marks}</div>
                  </div>
                  <div className="nb-card p-2 bg-[#93C5FD]">
                    <div className="text-[10px] uppercase mono">{isDay ? "Not-marked" : "Days"}</div>
                    <div className="display text-2xl font-black">{isDay ? notMarked : r.working_days}</div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="text-xs mono">Attendance rate: <b>{rate}%</b> ({r.present_marks}/{possible})</div>
                  <div className="mt-1 h-2 border-2 border-black bg-white rounded overflow-hidden">
                    <div className="h-full bg-[#86EFAC]" style={{ width: `${barW}%` }}/>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
